import { ActionsType, StoreType } from '@tock/shared/utils/connect';
import { Alert, RadioButton, Typography } from 'omakase';
import { Card } from 'ui';
import { core } from '@tock/coretypes';

import { ACTION_CHECKOUT_CHANGE_PAYMENT_METHOD } from 'utils/analytics/eventTypeConstantsAmplitude';
import { IconLogoApplePay } from '@tock/icons';
import { PaymentMethod } from '../PaymentMethod';
import { SelectCreditCardDetails } from '../SelectCreditCardDetails';
import { change } from 'redux-form';
import { paymentMethodWrapper } from './paymentMethodFormCssDefs';
import { paymentStack } from '../accept-terms/acceptTermsFormCssDefs';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useDispatch, useSelector } from 'react-redux';

export type PaymentMethod = 'APPLE_PAY' | 'CREDIT_CARD';

interface Props {
  actions: ActionsType;
  cards: core.PaymentCard[];
  creditCardFieldHasError: boolean;
  cvcField: ReduxFormField<string>;
  error?: string;
  isInitialized: boolean;
  isSubmitting: boolean;
  onRenderCreditCardForm(isSubmitting: boolean): JSX.Element;
  onSelectCard(storedPaymentCard: { value: string }): void;
  paymentMethod: ReduxFormField<PaymentMethod>;
  purchase?: StoreType['purchase'];
  selectedCard?: core.PaymentCard;
  shouldRevalidateSavedCreditCards: boolean;
  shouldSaveCreditCard: boolean;
  isApplePaySupported?: boolean;
}

export const PaymentMethodForm: React.VFC<Props> = ({
  actions,
  cards,
  cvcField,
  paymentMethod,
  error,
  isSubmitting,
  onRenderCreditCardForm,
  onSelectCard,
  purchase,
  selectedCard,
  shouldRevalidateSavedCreditCards,
  shouldSaveCreditCard,
  isInitialized,
  isApplePaySupported,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const currentBusiness = useSelector((state: StoreType) => state.app.config?.business);

  const dispatch = useDispatch();

  const handleChange = (method: PaymentMethod) => {
    dispatch(change('AcceptTermsForm', 'paymentMethod', method));

    amplitudeSendEvent(ACTION_CHECKOUT_CHANGE_PAYMENT_METHOD, {
      paymentMethod: method,
      previousPaymentMethod: paymentMethod.value,
    });
  };

  const priceInformation = purchase?.calculatedPrice?.ticketPriceInformation;
  const isCCHold = priceInformation?.priceType === 'PAYMENT_CARD_HOLD';
  const businessName = currentBusiness?.name;

  return (
    <div className="ConsumerCheckout-section">
      <div className="ConsumerCheckout-paymentHeader">
        <Typography component="h2" variant="body02Strong">
          {isCCHold ? 'Credit card required' : 'Choose payment method'}
        </Typography>
      </div>
      {isCCHold && businessName ? (
        <div>
          <Typography variant="body02" data-testid="cc-hold-required">
            {businessName} requires a credit card to secure a reservation.
          </Typography>
        </div>
      ) : null}
      {isApplePaySupported ? (
        <div css={paymentStack}>
          <Card variant="outlined">
            <PaymentMethod
              header={
                <div css={paymentMethodWrapper}>
                  <RadioButton
                    size="large"
                    value="APPLE_PAY"
                    label="Apple Pay"
                    name="paymentMethod"
                    checked={paymentMethod.value === 'APPLE_PAY'}
                    onChange={() => handleChange('APPLE_PAY')}
                    data-testid="apple-pay-option"
                  />
                  <IconLogoApplePay />
                </div>
              }
            />
            <PaymentMethod
              header={
                <div css={paymentMethodWrapper}>
                  <RadioButton
                    size="large"
                    value="CREDIT_CARD"
                    label="Credit card"
                    name="paymentMethod"
                    checked={paymentMethod.value === 'CREDIT_CARD'}
                    onChange={() => handleChange('CREDIT_CARD')}
                    data-testid="credit-card-option"
                  />
                </div>
              }
              body={
                paymentMethod.value === 'CREDIT_CARD' ? (
                  <SelectCreditCardDetails
                    actions={actions}
                    cards={cards}
                    cvcField={cvcField}
                    isSubmitting={isSubmitting}
                    onRenderCreditCardForm={onRenderCreditCardForm}
                    onSelectCard={onSelectCard}
                    shouldRevalidateSavedCreditCards={shouldRevalidateSavedCreditCards}
                    selectedCard={selectedCard}
                    shouldSaveCreditCard={shouldSaveCreditCard}
                    isInitialized={isInitialized}
                  />
                ) : null
              }
            />
          </Card>
        </div>
      ) : (
        <div className="ConsumerCheckout-paymentMethods">
          <SelectCreditCardDetails
            actions={actions}
            cards={cards}
            cvcField={cvcField}
            isSubmitting={isSubmitting}
            onRenderCreditCardForm={onRenderCreditCardForm}
            onSelectCard={onSelectCard}
            shouldRevalidateSavedCreditCards={shouldRevalidateSavedCreditCards}
            selectedCard={selectedCard}
            shouldSaveCreditCard={shouldSaveCreditCard}
            isInitialized={isInitialized}
          />
        </div>
      )}

      {paymentMethod.value !== 'APPLE_PAY' && purchase?.paymentError && (
        <div className="ConsumerCheckout-sectionError">
          <Alert role="alert" severity="error">
            {purchase.paymentError}
          </Alert>
        </div>
      )}
      {error && (
        <div className="Errors Errors--form">
          <div className="Error">{error}</div>
        </div>
      )}
    </div>
  );
};
